import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';


import * as styleUtils from '../../../../utils/styleUtils';

import TextInput from '../Input/TextInput';
import useOnOutsideClick from '../../../hooks/useOnOutsideClick';
import {I18nMessage} from '../../../contexts/I18n';
import {inputConstants} from '../Input';

import styles from './Select.scss';


// eslint-disable-next-line max-statements
function Select(
    {
        className,
        doShowPlaceholder,
        filter,
        id,
        name,
        onChange,
        options,
        placeholderI18n,
        searchPredicate,
        value
    }) {

    const {formatMessage} = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState({term: undefined});

    // apply filtered options
    let filteredOptions = options;
    if (filter) {
        filteredOptions = filteredOptions.filter(option => filter(option.data || option));
    }
    if (searchPredicate && search.term) {
        filteredOptions = filteredOptions.filter(option => searchPredicate(search, option.data));
    }

    // currently selected option only applies to filtered options
    const selectedOption = filteredOptions.find(option => option.value === value);

    // if previously selected option is not available anymore, reset value
    if (value && !selectedOption) {
        onChange(null);
    }

    // render for selected item using content, falls back to default label rendering
    const renderOption = option => {

        const {content, label, labelI18n} = option;

        return (
            <>
                {content && typeof content === 'function' && content() || content}
                {!content && (
                    <>
                        {label}
                        {labelI18n && formatMessage({id: labelI18n})}
                    </>
                )}
            </>
        );
    };

    // hide on outside clicks
    const wrapperRef = useRef(null);
    useOnOutsideClick(wrapperRef, () => {
        setIsOpen(false);
        setSearch({term: undefined});
    });

    // autofocus on search
    useEffect(() => {
        if (isOpen && searchPredicate) {
            wrapperRef.current.querySelector('input').focus();
        }
    }, [isOpen]);

    const getDynamicOptionsStyles = () => {

        const styles = {};
        if (wrapperRef.current) {
            const calculatedWidth = wrapperRef.current.getBoundingClientRect().width;
            // hardwired min-width check
            if (calculatedWidth >= 300) {
                styles.width = `${calculatedWidth}px`;
            }
        }

        return styles;
    };

    return (
        <div
            className={styleUtils.toClasses(styles.selectWrap, isOpen ? styles.open : null, className)}
            id={id}
            onClick={event => {

                event.preventDefault();
                event.stopPropagation();
                setSearch({term: undefined});
                setIsOpen(!isOpen);
            }}
            ref={wrapperRef}
        >

            <div className={styleUtils.toClasses(styles.select, selectedOption ? styles.hasValue : null)}>

                <div className={styleUtils.toClasses(styles.option, styles.selected)}>
                    {selectedOption && (selectedOption.selectedContent || renderOption(selectedOption))}
                    {!selectedOption && formatMessage({id: placeholderI18n})}
                </div>

                {/* hidden real select form for filling */}
                <select
                    name={name}
                    onChange={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        let selectedIndex = event.currentTarget.selectedIndex;
                        if (doShowPlaceholder) {
                            if (selectedIndex === 0) {

                                return;
                            }
                            selectedIndex -= 1;
                        }
                        const selectedOption = filteredOptions[selectedIndex];
                        onChange(selectedOption.value);
                    }}
                    value={value || ''}
                >
                    {doShowPlaceholder && (
                        <option value={''}>
                            <I18nMessage id={'form.select.placeholder'}/>
                        </option>
                    )}
                    {filteredOptions.map(option => (
                        <option
                            key={`select-element-option-${option.value}`}
                            value={option.value}
                        >
                            {
                                option.content
                                    ? `option-content-for-value-${option.value}`
                                    : renderOption(option)
                            }
                        </option>
                    ))}
                </select>

                {isOpen && (
                    <div
                        className={styles.options}
                        style={getDynamicOptionsStyles()}
                    >

                        {searchPredicate && (
                            <div
                                className={styleUtils.toClasses(styles.option, styles.filterSearch)}
                                onClick={event => event.stopPropagation()}
                            >
                                <TextInput
                                    name={`select-${name}-search`}
                                    onChange={term => {
                                        let searchTerm = (term || '').trim().toLowerCase();
                                        setSearch({term: searchTerm || undefined});
                                    }}
                                    placeholderI18n={'form.select.search.placeholder'}
                                    type={inputConstants.TYPE_TEXT}
                                    value={search.term}
                                />
                            </div>
                        )}

                        {!searchPredicate && doShowPlaceholder && (
                            <div className={styleUtils.toClasses(styles.option, styles.filterSearch)}>
                                <I18nMessage id={'form.select.placeholder'}/>
                            </div>
                        )}

                        {filteredOptions.map(option => (
                            <div
                                className={
                                    styleUtils.toClasses(
                                        styles.option,
                                        option.value === value
                                            ? styles.selectedOption
                                            : null
                                    )
                                }
                                data-value={option.value}
                                key={option.value}
                                onClick={event => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    onChange(option.value);
                                    setIsOpen(false);
                                }}
                            >
                                {renderOption(option)}
                            </div>
                        ))}

                    </div>
                )}
            </div>

        </div>
    );
}


Select.propTypes = {
    className: PropTypes.any,
    doShowPlaceholder: PropTypes.bool,
    filter: PropTypes.func,
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            content: PropTypes.oneOfType([
                PropTypes.element,
                PropTypes.func
            ]),
            data: PropTypes.object,
            label: PropTypes.string,
            labelI18n: PropTypes.string,
            selectedContent: PropTypes.oneOfType([
                PropTypes.element,
                PropTypes.func
            ]),
            value: PropTypes.any.isRequired
        })
    ),
    placeholderI18n: PropTypes.string,
    searchPredicate: PropTypes.func,
    value: PropTypes.any
};


Select.defaultProps = {
    doShowPlaceholder: true,
    name: 'select',
    options: [],
    placeholderI18n: 'form.select.placeholder'
};


export default Select;
